import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PublicLayout } from './BookPage';
import { useI18n } from '../i18n/I18nContext';
import { Newspaper, Music, Video, Mic, FileText, Download } from 'lucide-react';

const spring = { type: 'spring', stiffness: 300, damping: 20 };

const MEDIA_DOCS = [
  { key: 1, src: '/docs/dossier-partenariat.pdf', file: 'on-croit-qu-on-joue_dossier-partenariat.pdf' },
  { key: 3, src: '/docs/parents-jeunes-joueurs.pdf', file: 'on-croit-qu-on-joue_parents-jeunes-joueurs.pdf' },
  { key: 4, src: '/docs/cartes-reseaux-sociaux.pdf', file: 'on-croit-qu-on-joue_cartes-reseaux-sociaux.pdf' },
];

export default function MediaPage() {
  const { t } = useI18n();
  return (
    <PublicLayout testid="media-page" pageKey="page_medias">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={spring}>
        <p className="text-or-400 font-bold tracking-widest text-sm uppercase">{t('media.eyebrow')}</p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-creme mt-3">{t('media.title')}</h1>
        <p className="text-base sm:text-lg text-brume leading-relaxed mt-6 max-w-2xl">
          {t('media.intro')}
        </p>
      </motion.div>

      <div className="relative rounded-3xl overflow-hidden mt-10 ring-1 ring-or-400/20 shadow-[0_20px_50px_rgb(0,0,0,0.4)]">
        <img src="/images/eliano_stade.jpg" alt="Eliano marchant vers le stade" className="w-full h-auto object-cover" />
      </div>

      <div className="grid sm:grid-cols-2 gap-6 mt-10">
        {[
          { icon: Music, t: t('media.f1t'), d: t('media.f1d') },
          { icon: Mic, t: t('media.f2t'), d: t('media.f2d') },
          { icon: Video, t: t('media.f3t'), d: t('media.f3d') },
          { icon: Newspaper, t: t('media.f4t'), d: t('media.f4d'), to: '/presse' },
        ].map((f, i) => {
          const card = (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ ...spring, delay: 0.1 + i * 0.08 }}
              className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8 hover:border-or-400/40 transition-all h-full">
              <span className="w-12 h-12 rounded-2xl bg-or-400/15 flex items-center justify-center mb-5">
                <f.icon className="w-6 h-6 text-or-400" strokeWidth={2.5} />
              </span>
              <h3 className="text-xl font-semibold text-creme mb-2">{f.t}</h3>
              <p className="text-base text-brume leading-relaxed">{f.d}</p>
            </motion.div>
          );
          return f.to
            ? <Link key={f.t} to={f.to} data-testid="media-press-link" className="block">{card}</Link>
            : <div key={f.t}>{card}</div>;
        })}
      </div>

      <h2 className="text-2xl font-bold text-creme font-fredoka mt-12 mb-6" data-testid="media-docs-title">
        {t('media.docsTitle')}
      </h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {MEDIA_DOCS.map((d, i) => (
          <motion.div key={d.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ ...spring, delay: 0.1 + i * 0.08 }}
            className="bg-nuit-800 border border-nuit-700 rounded-3xl p-6 flex flex-col gap-4 hover:border-or-400/40 transition-colors"
            data-testid={`media-doc-${d.key}`}>
            <span className="w-14 h-14 shrink-0 rounded-2xl bg-or-400/15 flex items-center justify-center">
              <FileText className="w-7 h-7 text-or-400" strokeWidth={2.5} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-or-400 uppercase tracking-widest">{t(`press.aud${d.key}`)}</p>
              <p className="font-bold text-creme text-base">{t(`press.doc${d.key}`)}</p>
            </div>
            <a href={d.src} download={d.file} data-testid={`media-doc-download-${d.key}`}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold text-sm px-5 py-2.5 transition-colors">
              <Download className="w-4 h-4" strokeWidth={2.5} /> {t('press.download')}
            </a>
          </motion.div>
        ))}
      </div>
    </PublicLayout>
  );
}
